import React, { useState, useEffect } from 'react'
import moment from 'moment'
import TextHintWithIcon from '../../../../../Components/_newComponents/TextHintWithIcon'
import { styles } from './styles'
import {
  getAllowedDates,
  getAllowedWeekNumbers,
  prepareTableTotals,
} from './utils'
import WaitingForInputIllustration from '../../../../../Assets/Images/illustrations/WaitingForInputIllustration'
import TableRow from '../../../../../Components/_newComponents/TableParts/TableRow/TableRow'
import TableHeader from '../../../../../Components/_newComponents/TableParts/TableHeader/TableHeader'
import { useMemo } from 'react'
import TableTotals from '../../../../../Components/_newComponents/TableParts/TableTotals/TableTotals'
import MultiDateCalendar from '../../../../../Components/_newComponents/MultiDateCalendar'
import { headers } from './options'
import {
  getDatesWithDangerousWeather,
  getWeatherForLocationInRange,
} from '../../../../../Utils/Weather'
import { COUNTRY_CODES } from '../../../../../shared/constants'

const CustomCalendarTooltip = () => (
  <div
    style={{
      padding: '8px 16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
    }}
  >
    <p style={{ fontSize: '14px', fontWeight: 'bold' }}>
      <span role="img" aria-label="warning">
        ⚠️
      </span>{' '}
      Weather warning
    </p>
    <p style={{ fontSize: '12px' }}>
      This date is marked due to severe weather conditions (e.g., storms, smoke,
      or strong winds). For safety and reliability, we don’t recommend
      scheduling missions on this day.
    </p>
  </div>
)

const StepDate = ({
  classes,
  city,
  selectedDates,
  setSelectedDates,
  preparedData,
  setPreparedData,
}) => {
  const [datesWithDangerousWeather, setDatesWithDangerousWeather] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const today = moment()
  const { allowedDates, startDate, endDate } = getAllowedDates()
  const allowedWeekNumbers = getAllowedWeekNumbers()
  const calendarHeader = `${today.format('ddd, D MMM YYYY')} - W${today.format(
    'W',
  )}`

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        setIsLoading(true)
        const weatherData = await getWeatherForLocationInRange({
          latitude: city.location.latitude,
          longitude: city.location.longitude,
          from: startDate,
          to: endDate,
          cityId: city.id,
        })
        const datesWithDangerousWeather = getDatesWithDangerousWeather(
          weatherData,
        )
        setDatesWithDangerousWeather(datesWithDangerousWeather)
      } catch (error) {
        console.error('Error fetching weather data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchWeatherData()
  }, [])

  useEffect(() => {
    const prepareData = () => {
      const existingDataMap = new Map(preparedData.map(item => [item.id, item]))
      const sortedDates = selectedDates.sort((a, b) => {
        return moment(a).diff(moment(b))
      })
      const data = sortedDates.map(dateKey => {
        const date = moment(dateKey)
        if (existingDataMap.has(dateKey)) {
          return existingDataMap.get(dateKey)
        }

        return {
          id: dateKey,
          date: [COUNTRY_CODES.CANADA, COUNTRY_CODES.UNITED_STATES].includes(
            city.country.code,
          )
            ? `${date.format('MM.DD.YYYY dddd')}`
            : `${date.format('DD.MM.YYYY dddd')}`,
          morningMissions: 0,
          lunchMissions: 0,
          afternoonMissions: 0,
          deleteButton: true,
        }
      })
      setPreparedData(data)
    }
    prepareData()
  }, [selectedDates])

  const getSelectedWeekNumber = () => {
    if (selectedDates.length === 0) return null
    const firstSelectedDate = moment(selectedDates[0])
    return firstSelectedDate.format('W')
  }

  const handleDateToggle = date => {
    const key = date.format('YYYY-MM-DD')
    const selectedWeekNumber = getSelectedWeekNumber()
    const dateWeekNumber = date.format('W')

    if (!selectedWeekNumber) {
      setSelectedDates(prev => [...prev, key])
      return
    }

    if (dateWeekNumber === selectedWeekNumber) {
      setSelectedDates(prev =>
        prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key],
      )
      return
    }

    setSelectedDates([key])
  }

  const handleWeekToggle = week => {
    const weekKeys = week
      .filter(date => allowedDates.includes(date.format('YYYY-MM-DD')))
      .map(d => d.format('YYYY-MM-DD'))
    const allSelected = weekKeys.every(k => selectedDates.includes(k))

    if (allSelected) {
      setSelectedDates(prev => prev.filter(d => !weekKeys.includes(d)))
    } else {
      setSelectedDates(weekKeys)
    }
  }

  const calculateTotals = () => {
    const {
      morningMissions,
      lunchMissions,
      afternoonMissions,
      totalMissions,
    } = preparedData.reduce(
      (acc, item) => {
        return {
          morningMissions: acc.morningMissions + item.morningMissions,
          lunchMissions: acc.lunchMissions + item.lunchMissions,
          afternoonMissions: acc.afternoonMissions + item.afternoonMissions,
          totalMissions:
            acc.totalMissions +
            item.morningMissions +
            item.lunchMissions +
            item.afternoonMissions,
        }
      },
      {
        morningMissions: 0,
        lunchMissions: 0,
        afternoonMissions: 0,
        totalMissions: 0,
      },
    )

    const {
      morningMissionsPercentage,
      lunchMissionsPercentage,
      afternoonMissionsPercentage,
    } = {
      morningMissionsPercentage: totalMissions
        ? `${((morningMissions / totalMissions) * 100).toFixed(1)}%`
        : 0,
      lunchMissionsPercentage: totalMissions
        ? `${((lunchMissions / totalMissions) * 100).toFixed(1)}%`
        : 0,
      afternoonMissionsPercentage: totalMissions
        ? `${((afternoonMissions / totalMissions) * 100).toFixed(1)}%`
        : 0,
    }
    return {
      morningMissions,
      lunchMissions,
      afternoonMissions,
      totalMissions,
      morningMissionsPercentage,
      lunchMissionsPercentage,
      afternoonMissionsPercentage,
    }
  }

  const totals = useMemo(() => calculateTotals(), [preparedData])

  const tableTotalsData = useMemo(() => prepareTableTotals(totals), [totals])

  const handleCellEdit = (id, fieldName, value) => {
    setPreparedData(prev =>
      prev.map(item =>
        item.id === id ? { ...item, [fieldName]: value } : item,
      ),
    )
  }

  const handleRowDelete = id => {
    setSelectedDates(prev => prev.filter(d => d !== id))
  }

  return (
    <div style={styles.container}>
      <div>
        <div className={classes.contentTitleMain}>Select Dates</div>
        <div style={styles.descriptionContainer}>
          <p className={classes.contentDescription}>
            Please select dates from either of the next two available weeks —{' '}
            <u style={styles.descriptionUnderline}>
              all selected dates must be within the same week.
            </u>
          </p>
        </div>
        <div style={styles.flexRow}>
          <div>
            <MultiDateCalendar
              visibleRange={{
                start: moment()
                  .subtract(1, 'weeks')
                  .startOf('week'),
                end: moment()
                  .add(3, 'weeks')
                  .endOf('week'),
              }}
              selectedDates={selectedDates}
              allowedDates={allowedDates}
              allowedWeekNumbers={allowedWeekNumbers}
              onDateToggle={handleDateToggle}
              onWeekToggle={handleWeekToggle}
              header={calendarHeader}
              highlightedDates={datesWithDangerousWeather}
              tooltipContent={<CustomCalendarTooltip />}
              loading={isLoading}
            />
            <div style={styles.hintContainer}>
              <TextHintWithIcon
                text="You can unselect a date by either clicking on the date on the calendar or using the trash can icon."
                icon={styles.trashIcon}
              />
            </div>
          </div>
          <div style={styles.tableContainer}>
            <div className={classes.contentTitle}>Selected dates</div>
            <div className={classes.contentDescription}>
              The dates you selected are listed below, you can manage the amount
              of unique missions per time slot.
            </div>
            {selectedDates.length === 0 ? (
              <div style={styles.illustrationContainer}>
                <WaitingForInputIllustration />
              </div>
            ) : (
              <div>
                <TableHeader data={headers} />

                {preparedData.map(item => (
                  <TableRow
                    key={item.id}
                    data={item}
                    onCellEdit={handleCellEdit}
                    onRowDelete={handleRowDelete}
                    headers={headers}
                    isEdit={true}
                  />
                ))}
                {tableTotalsData && <TableTotals data={tableTotalsData} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StepDate
